import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import { analyticsService, hasDisabledDateRange, isTreeBlock } from 'venn-utils';
import { ColorUtils, GetColor, GROWTH_SIMULATOR_FAQ_HREF, Icon } from 'venn-ui-kit';
import { ConfirmationModal, SmallToggle, UserContext } from 'venn-components';
import { Bold, Emphasis, Message } from './sections/components/GlobalLinkedInfo';
import LinkableSetupSectionGroup from './sections/section-groups/LinkableSetupSectionGroup';
import GrowthSimulationSection from './sections/GrowthSimulationSection';
import MetricsSection from './sections/MetricsSection';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { blockSettings, customizedBlock, isReportState, rightPanelIsLinked, selectedBlockState } from 'venn-state';
import ScenarioSection from './sections/ScenarioSection';
import FormatSection from './sections/FormatSection';
import TitleSection from './sections/TitleSection';
import MoreSection from './sections/MoreSection';
import HoldingsTreeSection from './sections/HoldingsTreeSection';
import HoldingsBlockSection from './sections/HoldingsBlockSection';

export interface AnalysisBlockTabProps {
  selected: string;
}

/**
 * @deprecated this component will soon be replaced with {@link AnalysisBlockTabV2}.
 * If you make changes or fixes to thsi component, consider making those changes to AnalysisBlockTabV2 as well.
 */
const AnalysisBlockTab = ({ selected }: AnalysisBlockTabProps) => {
  const isReport = useRecoilValue(isReportState);
  const { hasPermission } = useContext(UserContext);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [linked, setLinked] = useRecoilState(rightPanelIsLinked);
  const setSelectedBlock = useSetRecoilState(selectedBlockState);
  const settings = useRecoilValue(blockSettings(selected));
  const block = useRecoilValue(customizedBlock(selected));

  const onToggleClick = () => {
    setLinked((current) => !current);
    analyticsService.ctaClicked({
      purpose: linked ? 'unlink block' : 'link block',
      locationOnPage: `'${settings.customBlockType} block settings toggle'`,
    });
  };

  return (
    <>
      {settings.customBlockType === 'GROWTH_SIMULATION' ? (
        <BlockDefiniton>
          <button type="button" onClick={() => window.open(GROWTH_SIMULATOR_FAQ_HREF, '_blank')}>
            <Icon type="up-right-from-square" /> Block Definition
          </button>
        </BlockDefiniton>
      ) : null}
      <BlockLinkedSection linked={!!linked}>
        <Icon type="chart-network" />

        <Message>
          These settings{' '}
          {linked ? (
            <>
              <Bold>link</Bold> to{' '}
            </>
          ) : (
            <>
              are <RedBold>unlinked</RedBold> from
            </>
          )}{' '}
          <LinkText onClick={() => setSelectedBlock(undefined)}>Global Configuration</LinkText>.
          <Emphasis>
            {linked
              ? 'Unlink to customize this block.'
              : `Link to apply ${isReport ? 'Report' : 'Studio'} level settings.`}
          </Emphasis>
        </Message>

        <SmallToggle
          toggled={!linked}
          onToggle={() => (linked ? onToggleClick() : setIsModalOpen(true))}
          size={18}
          toggledComponent={<GreyIcon type="link" />}
          unToggledComponent={<GreyIcon type="unlink" />}
          indicatorComponent={linked ? <TealIcon type="link" /> : <RedIcon type="unlink" />}
          noToggledHighlight
        />
      </BlockLinkedSection>
      <LinkableSetupSectionGroup
        hideDateRangeSection={hasDisabledDateRange(settings.customBlockType) && !linked}
        hideAllocatorLauncher={isReport}
      />
      {isModalOpen && !linked ? (
        <ConfirmationModal
          destructive
          className="link-block-confirmation"
          header="Confirmation"
          subhead={`Are you sure you want to link ${block.header || settings.title}?`}
          text={`Any unique block changes will be replaced by global ${isReport ? 'Report' : 'Studio'} settings.`}
          onCancel={() => setIsModalOpen(false)}
          onProceed={() => {
            onToggleClick();
            setIsModalOpen(false);
          }}
        />
      ) : null}
      {settings.customBlockType === 'GROWTH_SIMULATION' ? <GrowthSimulationSection selected={selected} /> : null}
      {isTreeBlock(settings.customBlockType) && (
        <HoldingsTreeSection selected={[]} blockType={settings.customBlockType} selectedRefId={selected} />
      )}
      {settings.customBlockType === 'ASSET_EXPOSURE' && <HoldingsBlockSection selectedRefId={selected} />}

      <ScenarioSection selected={selected} />
      <FormatSection selected={selected} isReadOnly={!hasPermission('STUDIO_EDIT_FORMAT')} />
      <MetricsSection selected={selected} />
      <TitleSection selected={selected} />
      <MoreSection selected={selected} />
    </>
  );
};

export default AnalysisBlockTab;

const BlockDefiniton = styled.div`
  color: ${GetColor.Primary.Dark};
  font-weight: bold;
  font-size: 16px;
  padding: 10px;
`;

const BlockLinkedSection = styled.div<{ linked: boolean }>`
  background-color: ${({ linked }) =>
    linked
      ? ColorUtils.opacifyFrom(GetColor.Primary.Dark, 0.1)
      : ColorUtils.opacifyFrom(GetColor.GreyScale.Grey10, 0.5)};
  display: flex;
  align-items: center;
  padding: 5px 15px;

  .qa-trades-toggle,
  .qa-trades-toggle > div {
    border-color: ${GetColor.GreyScale.Grey40};
  }
`;

const RedBold = styled(Bold)`
  color: ${GetColor.Error};
`;

const LinkText = styled.button`
  color: ${GetColor.Primary.Dark};
  font-weight: bold;
`;

const GreyIcon = styled(Icon)`
  color: ${GetColor.GreyScale.Grey70};
`;

const TealIcon = styled(Icon)`
  color: ${GetColor.Primary.Dark};
  width: 20px;
  text-align: center;
  line-height: 20px;
`;

const RedIcon = styled(Icon)`
  color: ${GetColor.Error};
  width: 20px;
  text-align: center;
  line-height: 20px;
`;
